import React, { useState, useEffect, useMemo, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  ThinTextAreaField,
  AddButton,
  ThinDropdown,
} from "../common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData, submitFormWithErrors } from "../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const OVERALL_STATUS_OPTIONS = [
  { label: "Completed", value: "Completed" },
  { label: "In progress", value: "In progress" },
  { label: "Delayed", value: "Delayed" },
  { label: "Postponed", value: "Postponed" },
  { label: "Cancelled", value: "Cancelled" },
];

const REMARKS_MAX = 500;

const EMPTY_SECOND_ENTRY = {
  show_second: false,
  second_overall_status: "",
  second_completion_date: "",
  second_extended_expiry_date: "",
  second_overall_remarks: "",
};

const toDateInput = (date) => (date ? String(date).split("T")[0] : "");

const localDate = (days = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
};

const toFormValues = (data = {}) => {
  const latest = {
    status: data?.overall_status || "",
    completion: toDateInput(data?.completion_date),
    extended: toDateInput(data?.extended_expiry_date),
    remarks: data?.overall_remarks || "",
  };

  if (!data?.initial_overall_status) {
    return {
      overall_status: latest.status,
      completion_date: latest.completion,
      extended_expiry_date: latest.extended,
      overall_remarks: latest.remarks,
      show_second: false,
      second_overall_status: "",
      second_completion_date: "",
      second_extended_expiry_date: "",
      second_overall_remarks: "",
    };
  }

  return {
    overall_status: data.initial_overall_status,
    completion_date: "",
    extended_expiry_date: toDateInput(data.initial_extended_expiry_date),
    overall_remarks: data.initial_overall_remarks || "",
    show_second: true,
    second_overall_status: latest.status,
    second_completion_date: latest.completion,
    second_extended_expiry_date: latest.extended,
    second_overall_remarks: latest.remarks,
  };
};

const toPayload = (values, trim = false) => {
  const remarks = (text) => (trim ? text?.trim() || null : text);
  const entry = (status, completion, extended, text) => ({
    overall_status: status,
    completion_date: status === "Completed" && completion ? completion : null,
    extended_expiry_date: status === "Delayed" && extended ? extended : null,
    overall_remarks: remarks(text),
  });

  if (values.show_second) {
    return {
      ...entry(
        values.second_overall_status,
        values.second_completion_date,
        values.second_extended_expiry_date,
        values.second_overall_remarks
      ),
      initial_overall_status: values.overall_status,
      initial_extended_expiry_date: values.extended_expiry_date || null,
      initial_overall_remarks: remarks(values.overall_remarks),
    };
  }

  return {
    ...entry(
      values.overall_status,
      values.completion_date,
      values.extended_expiry_date,
      values.overall_remarks
    ),
    initial_overall_status: null,
    initial_extended_expiry_date: null,
    initial_overall_remarks: null,
  };
};

const entrySchema = (prefix, statusField, savedDates, isActive) => {
  const active = (schema, rule) =>
    isActive
      ? schema.when(isActive.field, {
          is: isActive.is,
          then: rule,
          otherwise: (s) => s.nullable(),
        })
      : rule(schema);

  return {
    [`${prefix}overall_status`]: active(Yup.string(), (s) =>
      s.required("Overall status is required")
    ),
    [`${prefix}completion_date`]: Yup.string()
      .nullable()
      .when(statusField, {
        is: "Completed",
        then: (s) =>
          s
            .required("Completion date is required when status is completed")
            .test(
              "not-future",
              "Completion date cannot be in the future",
              (v) => !v || v <= localDate()
            ),
      }),
    [`${prefix}extended_expiry_date`]: Yup.string()
      .nullable()
      .when(statusField, {
        is: "Delayed",
        then: (s) =>
          s
            .required("Extended expiry date is required when status is delayed")
            .test(
              "future",
              "Extended expiry date must be a future date",
              (v) => !v || v > localDate() || savedDates.includes(v)
            ),
      }),
    [`${prefix}overall_remarks`]: active(Yup.string(), (s) =>
      s
        .required("Overall remarks are required")
        .max(REMARKS_MAX, `Overall remarks must not exceed ${REMARKS_MAX} characters`)
    ),
  };
};

const OverallProgressForm = ({
  apiBase,
  statusLabel,
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const initialValues = useMemo(() => toFormValues(initialData), [initialData]);

  const savedDates = useMemo(
    () =>
      isEditMode
        ? [
            initialValues.extended_expiry_date,
            initialValues.second_extended_expiry_date,
          ].filter(Boolean)
        : [],
    [isEditMode, initialValues]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        ...entrySchema("", "overall_status", savedDates),
        ...entrySchema("second_", "second_overall_status", savedDates, {
          field: "show_second",
          is: true,
        }),
      }),
    [savedDates]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: !!isEditMode,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  const isFormValid = validationSchema.isValidSync(formik.values);

  const storeRef = useRef(onStoreFormData);
  const validationRef = useRef(onValidationChange);
  storeRef.current = onStoreFormData;
  validationRef.current = onValidationChange;

  useEffect(() => {
    if (validationRef.current) validationRef.current(isFormValid);
  }, [isFormValid]);

  useEffect(() => {
    if (storeRef.current && !isEditMode) storeRef.current(toPayload(formik.values));
  }, [formik.values, isEditMode]);

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = toPayload(values, true);

      if (isEditMode && grantId) {
        const response = await fetchData(
          `${apiBase}/${grantId}`,
          "PUT",
          payload,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Overall Progress details updated successfully");
          if (onGrantUpdated) onGrantUpdated();
        }
      } else {
        if (onStoreFormData) onStoreFormData(payload);
        toast.success("Overall Progress details saved");
        if (onNext) onNext();
      }
    } catch (error) {
      console.error("Error saving overall progress details:", error);
      toast.error("Failed to save overall progress details");
    } finally {
      setLoading(false);
    }
  };

  const touchAll = () =>
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );

  const handleNext = () => {
    if (loading) return;
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        touchAll();
        return;
      }
      if (onStoreFormData) onStoreFormData(toPayload(formik.values, true));
      toast.success("Overall Progress details saved");
      if (onNext) onNext();
    });
  };

  const handleStatusChange = (prefix, value) => {
    formik.setFieldTouched(`${prefix}overall_status`, true, false);
    if (prefix === "" && value !== "Delayed") resetSecondTouched();
    formik.setValues((prev) => {
      const next = { ...prev, [`${prefix}overall_status`]: value };
      if (value !== "Completed") next[`${prefix}completion_date`] = "";
      if (value !== "Delayed") next[`${prefix}extended_expiry_date`] = "";
      return prefix === "" && value !== "Delayed"
        ? { ...next, ...EMPTY_SECOND_ENTRY }
        : next;
    });
  };

  const resetSecondTouched = () => {
    formik.setTouched(
      Object.keys(EMPTY_SECOND_ENTRY).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        { ...formik.touched }
      ),
      false
    );
  };

  const clearSecondEntry = () => {
    resetSecondTouched();
    formik.setValues((prev) => ({ ...prev, ...EMPTY_SECOND_ENTRY }));
  };

  const handleFieldChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    formik.setFieldTouched(fieldName, true, false);
  };

  const errorFor = (fieldName) =>
    formik.touched[fieldName] && formik.errors[fieldName];

  const renderError = (fieldName) =>
    errorFor(fieldName) && (
      <div className="text-red-500 text-sm mt-1">{formik.errors[fieldName]}</div>
    );

  const renderEntry = (prefix) => {
    const status = formik.values[`${prefix}overall_status`];
    const statusField = `${prefix}overall_status`;
    const completionField = `${prefix}completion_date`;
    const extendedField = `${prefix}extended_expiry_date`;
    const remarksField = `${prefix}overall_remarks`;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div>
          <ThinDropdown
            label={statusLabel}
            id={`${prefix}overall-status-dropdown`}
            options={OVERALL_STATUS_OPTIONS}
            value={status}
            onChange={(e) => handleStatusChange(prefix, e.target.value)}
            name={statusField}
            error={errorFor(statusField)}
          />
          {renderError(statusField)}
        </div>

        {status === "Completed" && (
          <div>
            <ThinTextField
              label="Date of Completion"
              name={completionField}
              type="date"
              max={localDate()}
              value={formik.values[completionField]}
              onChange={(e) => handleFieldChange(completionField, e.target.value)}
              onBlur={() => formik.setFieldTouched(completionField, true)}
              error={errorFor(completionField)}
            />
            {renderError(completionField)}
          </div>
        )}

        {status === "Delayed" && (
          <div>
            <ThinTextField
              label="Extended Expiry Date"
              name={extendedField}
              type="date"
              min={localDate(1)}
              value={formik.values[extendedField]}
              onChange={(e) => handleFieldChange(extendedField, e.target.value)}
              onBlur={() => formik.setFieldTouched(extendedField, true)}
              error={errorFor(extendedField)}
            />
            {renderError(extendedField)}
          </div>
        )}

        <div className="md:col-span-2">
          <ThinTextAreaField
            label="Remarks (Overall) - Any deviation or issues/challenges"
            name={remarksField}
            value={formik.values[remarksField]}
            onChange={(e) => handleFieldChange(remarksField, e.target.value)}
            onBlur={() => formik.setFieldTouched(remarksField, true)}
            placeholder="Enter overall remarks, deviations, issues, or challenges..."
            rows={4}
            maxLength={REMARKS_MAX}
            error={errorFor(remarksField)}
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {(formik.values[remarksField] || "").length} / {REMARKS_MAX} characters
          </div>
          {renderError(remarksField)}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-4">
      {renderEntry("")}

      {formik.values.overall_status === "Delayed" && !formik.values.show_second && (
        <div>
          <button
            type="button"
            onClick={() => formik.setFieldValue("show_second", true)}
            className="border border-teal-600 text-teal-600 rounded-lg px-4 py-2 hover:bg-teal-50 transition-colors"
          >
            + Add more
          </button>
        </div>
      )}

      {formik.values.show_second && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700">
              Overall Progress (Update)
            </h4>
            <button
              type="button"
              onClick={clearSecondEntry}
              className="text-red-600 text-sm hover:underline"
            >
              Remove
            </button>
          </div>
          {renderEntry("second_")}
        </div>
      )}

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNext}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Saving..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default OverallProgressForm;
